import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import type {
  Driver as SharedDriver,
  DriverStatus,
  VehicleType,
  CreateDriverInput as SharedCreateDriverInput,
  UpdateDriverInput as SharedUpdateDriverInput,
} from '@shinkansen/graphql'

// Re-export types from shared package for convenience
export type { DriverStatus, VehicleType }

/**
 * Driver interface - using shared type from GraphQL schema
 */
export type Driver = SharedDriver

const DRIVER_FIELDS = `
  id
  userId
  licenseNumber
  vehicleType
  vehiclePlate
  status
  currentLat
  currentLng
  maxCapacityKg
  rating
  totalDeliveries
  createdAt
  updatedAt
`

/**
 * Hook to fetch all drivers.
 * Returns TanStack Query result with drivers array.
 *
 * @returns TanStack Query result with Driver[] data
 */
export function useDrivers() {
  return useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const result = await graphqlRequest<{ drivers: Driver[] }>(`
        query Drivers {
          drivers {
            ${DRIVER_FIELDS}
          }
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.drivers ?? []
    },
  })
}

/**
 * Hook to fetch available drivers only.
 * Returns TanStack Query result with available drivers array.
 *
 * @returns TanStack Query result with Driver[] data (only AVAILABLE status)
 */
export function useAvailableDrivers() {
  return useQuery({
    queryKey: ['availableDrivers'],
    queryFn: async () => {
      const result = await graphqlRequest<{ availableDrivers: Driver[] }>(`
        query AvailableDrivers {
          availableDrivers {
            ${DRIVER_FIELDS}
          }
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.availableDrivers ?? []
    },
  })
}

// ============== Mutation Types ==============

/**
 * Input for creating a new driver - using shared type from GraphQL schema
 */
export type CreateDriverInput = SharedCreateDriverInput

/**
 * Input for updating a driver - using shared type from GraphQL schema
 */
export type UpdateDriverInput = SharedUpdateDriverInput

// ============== Mutation Hooks ==============

/**
 * Hook to create a new driver
 */
export function useCreateDriver() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateDriverInput) => {
      const result = await graphqlRequest<{ createDriver: Driver }>(`
        mutation CreateDriver($input: CreateDriverInput!) {
          createDriver(input: $input) {
            ${DRIVER_FIELDS}
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.createDriver
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
    },
  })
}

/**
 * Hook to update an existing driver
 */
export function useUpdateDriver() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateDriverInput) => {
      const result = await graphqlRequest<{ updateDriver: Driver }>(`
        mutation UpdateDriver($input: UpdateDriverInput!) {
          updateDriver(input: $input) {
            ${DRIVER_FIELDS}
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updateDriver
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
    },
  })
}

/**
 * Hook to delete a driver
 */
export function useDeleteDriver() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await graphqlRequest<{ deleteDriver: boolean }>(`
        mutation DeleteDriver($id: UUID!) {
          deleteDriver(id: $id)
        }
      `, { id })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deleteDriver
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
    },
  })
}
